/**
 * TemplateService — Brand-Template-Endpunkte über die http-Instanz (Spec Task 12).
 * Die http-Instanz entpackt `response.data` bereits im Interceptor, daher
 * geben die Methoden die Nutzdaten direkt zurück.
 */

import http from './http'

export interface TemplateSummary {
  id: string
  name: string
  version: string
  contractVersion: number
  status: 'draft' | 'active' | 'archived'
  capabilities: string[]
  requiredFields: string[]
  createdAt?: string
}

export interface TemplateStatus {
  active: TemplateSummary | null
  draft: TemplateSummary | null
  fieldAllowlist: string[]
  defaultAllowlist: string[]
  contractFields: string[]
}

/**
 * Holt den Template-Status (aktives + Entwurf-Template, Allowlists) einer Brand.
 *
 * @param brandKey Brand-Schlüssel
 * @returns Template-Status der Brand
 */
async function getStatus(brandKey: string): Promise<TemplateStatus> {
  return http.get(`/brands/${brandKey}/template`)
}

/**
 * Lädt ein Template-ZIP als neuen Entwurf hoch.
 *
 * @param brandKey Brand-Schlüssel
 * @param file Hochzuladendes ZIP-Paket
 * @returns Zusammenfassung des neu angelegten Entwurf-Templates
 */
async function uploadDraft(brandKey: string, file: File): Promise<TemplateSummary> {
  const form = new FormData()
  form.append('file', file)
  return http.post(`/brands/${brandKey}/template`, form)
}

/**
 * Aktiviert den aktuellen Entwurf einer Brand als aktives Template.
 *
 * @param brandKey Brand-Schlüssel
 * @returns ID des neu aktiven Templates
 */
async function activate(brandKey: string): Promise<{ activeTemplateId: string }> {
  return http.post(`/brands/${brandKey}/template/activate`)
}

/**
 * Erzeugt ein zeitlich begrenztes Preview-Token für das Entwurf-Template.
 *
 * @param brandKey Brand-Schlüssel
 * @returns Token samt Ablaufzeitpunkt
 */
async function previewToken(brandKey: string): Promise<{ token: string; expiresAt: string }> {
  return http.get(`/brands/${brandKey}/template/preview-token`)
}

export const TemplateService = {
  getStatus,
  uploadDraft,
  activate,
  previewToken,
}
